/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/anchor-has-content */
// * Importa o React
import { useEffect, useRef, useState, useMemo, useCallback } from "react";

// * Importing the hooks from react-router-dom
import { useNavigate, useLocation, useParams } from "react-router-dom";

// * Importing the Material UI components
import { Tab, Tabs } from "@material-ui/core";
import { RiArrowGoBackLine } from "react-icons/ri";

// * Importing the components
import DefaultButton from "../../../component/base/DefaultButton";
import { TextReadMore } from "../../../component/base/TextReadMore";
import TabPanel from "../../../component/base/TabPanel";
import { Grid } from "../../../component/list/grid";
import { Alert } from "../../../component/list/alert";
import { Loading } from "../../../component/common/Loading";

// * Importing the moment library
import moment from "moment";

//? Importing the useApplications hook
import { useApplications } from "../../../hooks/applications/useApplications";
import { useApplicationStudents } from "../../../hooks/applications/applicationsStudents/useApplicationStudent";
import { useApplicationTests } from "../../../hooks/applications/applicationsTests/useApplicationTest";
import { useApplicationDiagrams } from "../../../hooks/applications/applicationDiagrams/useApplicationDiagrams";
import { useReviewers } from "../../../hooks/applications/Reviewers/useReviewers";

// Função auxiliar para query string
function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export function ApplicationManagerView() {
  const navigate = useNavigate();
  const params = useParams();
  const query = useQuery();

  const applicationId = params.id ?? "";
  const token = localStorage.getItem("token") || "";

  const { getApplicationById } = useApplications(token);
  const [applicationModel, setApplicationModel] = useState<any>(null);

  const {
    applicationStudents,
    loading: loadingStudents,
    deleteApplicationStudent,
  } = useApplicationStudents(applicationId, token);

  const {
    reviewers,
    loading: loadingReviewers,
    deleteReviewer,
  } = useReviewers(applicationId, token);

  const {
    applicationTests,
    loading: loadingTests,
    deleteApplicationTest,
  } = useApplicationTests(applicationId, token);


  const {
    applicationDiagrams,
    loading: loadingDiagrams,
    deleteApplicationDiagram,
    getNavigationRoute
  } = useApplicationDiagrams(applicationId, token);



  // Estados de alerta e remoção
  const [showWarning, setShowWarning] = useState(false);
  const [showReviewerWarning, setShowReviewerWarning] = useState(false);
  const [showTestWarning, setShowTestWarning] = useState(false);
  const [showDiagramWarning, setShowDiagramWarning] = useState(false);


  const [studentIdToRemove, setStudentIdToRemove] = useState<string | null>(
    null
  );
  const [reviewerIdToRemove, setReviewerIdToRemove] = useState<string | null>(
    null
  );
  const [testIdToRemove, setTestIdToRemove] = useState<string | null>(null);
  const [diagramIdToRemove, setDiagramIdToRemove] = useState<string | null>(null);
  
  // Estados de busca
  const [searchStudent, setSearchStudent] = useState<string>("");
  const [searchReviewer, setSearchReviewer] = useState<string>("");
  const [searchTest, setSearchTest] = useState<string>("");
  const [searchDiagram, setSearchDiagram] = useState<string>("");

  // Aba ativa
  const [activeTab, setActiveTab] = useState(0);

  const timeoutJob = useRef<any>(null);

  useEffect(() => {
    async function fetchApplication() {
      if (!applicationId) return;
      try {
        const data = await getApplicationById(applicationId);
        if (data) setApplicationModel(data);
      } catch (error) {
        console.error("Error fetching application:", error);
      }
    }
    fetchApplication();

    const tabParam = query.get("tab");
    if (tabParam) {
      setActiveTab(Number(tabParam));
    }

    const currentTimeoutJob = timeoutJob.current;
    return () => {
      clearTimeout(currentTimeoutJob);
    };
  }, [applicationId, getApplicationById, query]);

  // Se aplicação é online, não acessa a 4ª aba
  useEffect(() => {
    if (applicationModel?.isOnline && activeTab === 3) {
      setActiveTab(0);
    }
  }, [applicationModel, activeTab]);

  // Callbacks de remoção
  const handleRemoveStudent = useCallback(async () => {
    if (!studentIdToRemove) return;
    try {
      await deleteApplicationStudent(studentIdToRemove);
      setShowWarning(false);
      setStudentIdToRemove(null);
    } catch (error) {
      console.error("Error removing student:", error);
    }
  }, [studentIdToRemove, deleteApplicationStudent]);

  const handleRemoveReviewer = useCallback(async () => {
    if (!reviewerIdToRemove) return;
    try {
      await deleteReviewer(reviewerIdToRemove);
      setShowReviewerWarning(false);
      setReviewerIdToRemove(null);
    } catch (error) {
      console.error("Error removing reviewer:", error);
    }
  }, [reviewerIdToRemove, deleteReviewer]);

  const handleRemoveTest = useCallback(async () => {
    if (!testIdToRemove) return;
    try {
      await deleteApplicationTest(testIdToRemove);
      setShowTestWarning(false);
      setTestIdToRemove(null);
    } catch (error) {
      console.error("Error removing test:", error);
    }
  }, [testIdToRemove, deleteApplicationTest]);


  const handleRemoveDiagram = useCallback(async () => {
    if (!diagramIdToRemove) return;
    try {
      await deleteApplicationDiagram(diagramIdToRemove);
      setShowDiagramWarning(false);
      setDiagramIdToRemove(null);
    } catch (error) {
      console.error("Error removing diagram:", error);
    }
  }, [diagramIdToRemove, deleteApplicationDiagram]);

  // Filtragem (caso queira filtrar)
  const filteredStudents = useMemo(() => {
    return applicationStudents.filter((item) =>
      item.registration?.student?.name
        ?.toLowerCase()
        .includes(searchStudent.toLowerCase())
    );
  }, [applicationStudents, searchStudent]);

  const filteredReviewers = useMemo(() => {
    return reviewers.filter((rev) =>
      rev.user?.name?.toLowerCase().includes(searchReviewer.toLowerCase())
    );
  }, [reviewers, searchReviewer]);

  const filteredTests = useMemo(() => {
    return applicationTests.filter((testItem) =>
      testItem.test?.name?.toLowerCase().includes(searchTest.toLowerCase())
    );
  }, [applicationTests, searchTest]);

  const filteredDiagrams = useMemo(() => {
    return applicationDiagrams.filter((diagramItem) =>
      diagramItem.diagram?.pageTitle?.toLowerCase().includes(searchDiagram.toLowerCase())
    );
  }, [applicationDiagrams, searchDiagram]);

  // Mudança de abas
  const handleChangeTab = useCallback(
    (_:any, newValue: number) => {
      setActiveTab(newValue);
      navigate({ search: `?tab=${newValue}` });
    },
    [navigate]
  );

  if (!applicationModel) {
    return <Loading data-testid="loading-application-manager" message="Carregando informações da aplicação..." />;
  }

  return (
    <div data-testid="application-manager-view" style={{ position: "relative", padding: "1rem" }}>
      <div
        className="card"
        style={{
          border: "1px solid #e0e0e0",
          boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
          borderRadius: "6px",
        }}
      >
        <div className="card-body" style={{ padding: "1.5rem" }}>
          {/* ALERTS */}
          <Alert
            title="Atenção"
            text="Ao desvincular este aluno, ele não terá mais acesso a esta prova."
            show_warning={showWarning}
            onCancel={() => setShowWarning(false)}
            onConfirm={handleRemoveStudent}
          />
          <Alert
            title="Atenção"
            text="Ao desvincular este corretor, todas as suas correções serão apagadas."
            show_warning={showReviewerWarning}
            onCancel={() => setShowReviewerWarning(false)}
            onConfirm={handleRemoveReviewer}
          />
          <Alert
            title="Atenção"
            text="Ao desvincular esta prova, todas as suas correções serão apagadas."
            show_warning={showTestWarning}
            onCancel={() => setShowTestWarning(false)}
            onConfirm={handleRemoveTest}
          />
          <Alert
            title="Atenção"
            text="Ao desvincular esta diagramação, todas as suas correções serão apagadas."
            show_warning={showDiagramWarning}
            onCancel={() => setShowDiagramWarning(false)}
            onConfirm={handleRemoveDiagram}
          />

          {/* HEADER / TÍTULO */}
          <div
            style={{
              backgroundColor: "#f5f5f5",
              padding: "1rem",
              borderRadius: "4px",
              marginBottom: "1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h2 style={{ margin: 0, fontSize: "1.25rem" }} data-testid="application-name">
              {applicationModel.name}
            </h2>
            {/* Botão Voltar posicionado à direita */}
            <DefaultButton
              bg="secondary"
              text="Voltar"
              icon={<RiArrowGoBackLine />}
              to="/tests"
              style={{ marginLeft: "0.75rem" }}
            />
          </div>

          {/* INFORMAÇÕES PRINCIPAIS DA APLICAÇÃO */}
          <div
            style={{
              marginBottom: "1.5rem",
              backgroundColor: "#fff",
              padding: "1rem",
              borderRadius: "4px",
              border: "1px solid #ddd",
            }}
          >
            {applicationModel.description && (
              <TextReadMore
                title="Descrição: "
                element="div"
                limit={300}
                style={{ width: "100%", whiteSpace: "pre-wrap" }}
              >
                {applicationModel.description}
              </TextReadMore>
            )}
            <p style={{ margin: "0.5rem 0" }}>
              <strong>Tipo:</strong>{" "}
              {applicationModel.isOnline ? "Online" : "Presencial"}
            </p>
            <p style={{ margin: "0.5rem 0" }}>
              <strong>Status:</strong>{" "}
              {(() => {
                switch (applicationModel.status) {
                  case "pending":
                    return "Criada";
                  case "in_process":
                    return "Em Progresso";
                  case "finished":
                    return "Finalizada";
                  case "cancelled":
                    return "Cancelada";
                  default:
                    return applicationModel.status;
                }
              })()}
            </p>
            <p style={{ margin: "0.5rem 0" }}>
              <strong>Data de Início:</strong>{" "}
              {applicationModel.startDate
                ? moment(applicationModel.startDate).format("DD/MM/YYYY HH:mm")
                : "N/A"}
            </p>
            <p style={{ margin: "0.5rem 0" }}>
              <strong>Data de Término:</strong>{" "}
              {applicationModel.endDate
                ? moment(applicationModel.endDate).format("DD/MM/YYYY HH:mm")
                : "N/A"}
            </p>
            <p style={{ margin: "0.5rem 0" }}>
              <strong>Data Limite:</strong>{" "}
              {applicationModel.deadlineDate
                ? moment(applicationModel.deadlineDate).format(
                    "DD/MM/YYYY HH:mm"
                  )
                : "N/A"}
            </p>
          </div>

          {/* LINK INVISÍVEL PARA DOWNLOAD, SE PRECISAR */}
          <a id="download_link" target="_blank" rel="noreferrer" />

          {/* TABS */}
          <Tabs
            value={activeTab}
            onChange={handleChangeTab}
            style={{ backgroundColor: "#fafafa", marginBottom: "1rem" }}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="on"
          >
            <Tab data-testid="tab-alunos" label="Alunos" />
            <Tab data-testid="tab-corretores" label="Corretores" />
            <Tab data-testid="tab-provas" label="Provas" />
            {!applicationModel.isOnline && <Tab data-testid="tab-diagramacoes" label="Diagramações" />}
          </Tabs>

          {/* TAB 0: ALUNOS */}
          <TabPanel value={activeTab} index={0}>
            <Grid
              onChangeSearchInput={setSearchStudent}
              component={null}
              search_input_value={searchStudent}
              loading_search={false}
              fields={[
                { name: "name", type: "text", header: "Nome" },
                { name: "email", type: "text", header: "E-mail" },
                { name: "unitName", type: "text", header: "Unidade" },
                { name: "courseName", type: "text", header: "Curso" },
                { name: "period", type: "text", header: "Período" },
              ]}
              items={filteredStudents.map((x) => ({
                _id: x.id,
                name: x.registration?.student?.name ?? "",
                email: x.registration?.student?.email ?? "",
                unitName: x.registration?.team?.unit ?? "Não informado",
                courseName: x.registration?.team?.course ?? "Não informado",
                period: x.registration?.team?.is_first_semester ? "1" : "2",
              }))}
              onManagerActionClick={(id) => {
                window.open(
                  `${window.location.origin}/tests/view/${applicationId}/${id}`
                );
              }}
              hideManager
              onDeleteActionClick={(id: string) => {
                setStudentIdToRemove(id);
                setShowWarning(true);
              }}
              hideEdit
              onEditActionClick={(id) => {
                navigate(`/tests/show/${applicationId}/${id}`);
              }}
              not_found_message="Nenhum aluno encontrado."
              loading_screen={loadingStudents}
              button_text="Adicionar alunos"
              button_link={`/tests/students/${applicationId}/create`}
              search_text="Pesquisar pelo nome do aluno"
              pagination={{ last_page: 1, current_page: 1 }}
              onChangePagination={() => {}}
              top_text={`Aqui ficam os alunos vinculados à aplicação ${applicationModel.name}`}
              onSearchButtonClick={() => {}}
            />
          </TabPanel>

          {/* TAB 1: CORRETORES */}
          <TabPanel value={activeTab} index={1}>
            <Grid
              hideManager
              onChangeSearchInput={setSearchReviewer}
              component={null}
              search_input_value={searchReviewer}
              loading_search={false}
              fields={[
                { name: "reviewerName", type: "text", header: "Nome" },
                { name: "reviewerEmail", type: "text", header: "E-mail" },
              ]}
              items={filteredReviewers.map((rev) => ({
                _id: rev.id,
                reviewerName: rev.user?.name ?? "",
                reviewerEmail: rev.user?.email ?? "N/A",
              }))}
              onManagerActionClick={(id) => {
                window.open(
                  `${window.location.origin}/tests/view/${applicationId}/${id}`
                );
              }}
              onDeleteActionClick={(id: string) => {
                setReviewerIdToRemove(id);
                setShowReviewerWarning(true);
              }}
              hideEdit
              onEditActionClick={(id) => {
                navigate(`/tests/show/${applicationId}/${id}`);
              }}
              not_found_message="Nenhum corretor encontrado."
              loading_screen={loadingReviewers}
              button_text="Adicionar corretores"
              button_link={`/tests/reviewers/${applicationId}/create`}
              search_text="Pesquisar pelo nome do corretor"
              pagination={{ last_page: 1, current_page: 1 }}
              onChangePagination={() => {}}
              top_text={`Aqui ficam os corretores vinculados à aplicação ${applicationModel.name}`}
              onSearchButtonClick={() => {}}
            />
          </TabPanel>

          {/* TAB 2: PROVAS */}
          <TabPanel value={activeTab} index={2}>
            <Grid
              onChangeSearchInput={setSearchTest}
              component={null}
              search_input_value={searchTest}
              hideManager
              loading_search={false}
              fields={[
                { name: "name", type: "text", header: "Nome" },
                { name: "createdBy", type: "text", header: "Criado Por" },
                { name: "createdAt", type: "text", header: "Data de Criação" },
              ]}
              items={filteredTests.map((applicationTest) => ({
                _id: applicationTest.id,
                name: applicationTest.test?.name ?? "",
                createdBy: applicationTest.test?.createdBy ?? "N/A",
                createdAt: moment(applicationTest.createdAt).format(
                  "DD/MM/YYYY HH:mm"
                ),
              }))}
              onManagerActionClick={(id) => {
                window.open(
                  `${window.location.origin}/tests/view/${applicationId}/${id}`
                );
              }}
              onDeleteActionClick={(id: string) => {
                setTestIdToRemove(id);
                setShowTestWarning(true);
              }}
              onEditActionClick={(id) => {
                if (applicationTests[0]?.test?.id) {
                  navigate(`/exams/edit/${applicationTests[0].test.id}`);
                }
              }}
              not_found_message="Nenhuma prova encontrada."
              loading_screen={loadingTests}
              button_text="Adicionar Provas"
              button_link={`/tests/exam/${applicationId}/create`}
              search_text="Pesquisar pelo nome da prova"
              pagination={{ last_page: 1, current_page: 1 }}
              onChangePagination={() => {}}
              top_text={`Aqui ficam as provas vinculadas à aplicação ${applicationModel.name}`}
              onSearchButtonClick={() => {}}
            />
          </TabPanel>

          {/* TAB 3: DIAGRAMAÇÕES (Só renderiza se não for online) */}
          {!applicationModel.isOnline && (
            <TabPanel value={activeTab} index={3}>
              <Grid
              onChangeSearchInput={setSearchDiagram}
              component={null}
              search_input_value={searchDiagram}
              hideManager
              showAuxiliar1
              showCorrection
              loading_search={false}
              fields={[
                {
                name: "pageTitle",
                type: "text",
                header: "Título da Página",
                },
                { name: "testName", type: "text", header: "Nome do Teste" },
                {
                name: "createdAt",
                type: "date",
                header: "Data de Criação",
                },
                {
                name: "type",
                type: "text",
                header: "Tipo",
                },
              ]}
              items={filteredDiagrams.map((applicationDiagram) => ({
                _id: applicationDiagram.id,
                pageTitle: applicationDiagram.diagram?.pageTitle ?? "",
                diagramId: applicationDiagram.diagram?.id ?? "",
                testName: applicationDiagram.diagram?.testName ?? "N/A",
                createdAt: moment(applicationDiagram.diagram?.createdAt),
                type: applicationDiagram.diagram?.type === "dissertative" ? "Dissertativa" : "Objetiva",
              }))}
              onManagerActionClick={(id) => {
                const diagram = applicationDiagrams.find(d => d.id === id);
                if (diagram) {
                  if (diagram.hasCorrection && diagram.latestCorrection) {
                    navigate(getNavigationRoute(diagram));
                  } else if (diagram.diagram?.id) {
                    window.open(`${window.location.origin}/tests/view/${applicationId}/${diagram.diagram.id}`);
                  }
                }
              }}
              onDeleteActionClick={(id: string) => {
                setDiagramIdToRemove(id);
                setShowDiagramWarning(true);
              }}
              onEditActionClick={(id) => {
                const diagram = applicationDiagrams.find(d => d.id === id);
                if (diagram?.diagram?.id) {
                navigate(`/layouts_default_exams/edit/${diagram.diagram.id}`);
                }
              }}
              onAuxiliar1Click={(id) => {
                const diagram = applicationDiagrams.find(d => d.id === id);
                if (!diagram || !diagram.diagram?.id) return;

                if (diagram.diagram.type === "dissertative") {
                navigate(
                  `/tests/view/dissertives/${applicationId}/diagrams/view/${diagram.diagram.id}`
                );
                } else {
                if (diagram.answerSheetId) {
                  navigate(
                  `/tests/view/objectives/${applicationId}/diagrams/edit/${diagram.answerSheetId}/${diagram.diagram.id}`
                  );
                } else {
                  navigate(
                  `/tests/view/objectives/${applicationId}/diagrams/create/${diagram.diagram.id}`
                  );
                }
                }
              }}
              onCorrectionActionClick={
                (id) => {
                const diagram = applicationDiagrams.find(d => d.id === id);
                if (diagram?.diagram?.id) {
                  if (diagram.hasCorrection && diagram.latestCorrection) {
                    navigate(
                      `/tests/corrections/view/${applicationId}/${diagram.diagram.id}/${diagram.latestCorrection.id}`
                    );
                  } else {
                    navigate(
                      `/tests/corrections/create/${applicationId}/${diagram.diagram.id}`
                    );
                  }
                }
                }
              }
              not_found_message="Nenhuma diagramação encontrada."
              loading_screen={loadingDiagrams}
              button_text="Adicionar Diagramação"
              button_link={`/tests/diagrams/${applicationId}/create`}
              search_text="Pesquisar pelo nome de uma diagramação"
              pagination={{ last_page: 1, current_page: 1 }}
              onChangePagination={() => {}}
              top_text={`Aqui ficam as diagramações vinculados à aplicação ${applicationModel.name}`}
              onSearchButtonClick={() => {}}
              />
            </TabPanel>
          )}
        </div>
      </div>
    </div>
  );
}
